/**
 * Governance Loop Anchor Screen (F090)
 *
 * Live system diagram showing the LeftShield runtime governance engine:
 * Signals > Policy > Enforcement > Evidence > Assurance > Intelligence
 *
 * Hexagonal loop layout with animated signal dots, system heartbeat,
 * and live telemetry per stage.
 *
 * Route: /governance-loop | Nav: Command > Governance Loop
 */
import React from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  tokens,
  makeStyles,
  Badge,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  ShieldCheckmarkRegular,
  ShieldLockRegular,
  CertificateRegular,
  GridRegular,
  LightbulbRegular,
  ShareAndroidRegular,
  HeartPulseRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';
import { useDashboardOverview } from '../hooks/useDashboardOverview';
import { solutionTokens, semanticColors } from '../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Stage colors                                                       */
/* ------------------------------------------------------------------ */
const STAGE_COLORS = {
  signals: semanticColors.warning,
  policy: governanceTokens.colorGovernancePrimary,
  enforcement: governanceTokens.colorGovernanceSecondary,
  evidence: tokens.colorBrandBackground,
  assurance: tokens.colorBrandBackground,
  intelligence: solutionTokens.brandSecondary,
} as const;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface LoopStage {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  route: string;
  metrics: { label: string; value: string }[];
}

/* ------------------------------------------------------------------ */
/*  Keyframe CSS (injected once)                                       */
/* ------------------------------------------------------------------ */
const KEYFRAME_ID = 'governance-loop-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(KEYFRAME_ID)) {
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.textContent = `
    @keyframes glFlowDot {
      0%   { offset-distance: 0%; opacity: 0; }
      10%  { opacity: 1; }
      90%  { opacity: 1; }
      100% { offset-distance: 100%; opacity: 0; }
    }
    @keyframes glPulse {
      0%, 100% { transform: scale(1); opacity: 0.6; }
      50%      { transform: scale(1.35); opacity: 1; }
    }
    @keyframes glGlow {
      0%, 100% { box-shadow: 0 0 8px ${governanceTokens.colorGovernancePrimary}4D; }
      50%      { box-shadow: 0 0 20px ${governanceTokens.colorGovernancePrimary}99; }
    }
  `;
  document.head.appendChild(style);
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const useStyles = makeStyles({
  heartbeatCard: {
    position: 'relative',
    overflow: 'hidden',
  },
  heartbeatGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingHorizontalL,
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  heartbeatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    textAlign: 'center' as const,
  },
  heartbeatValue: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase500,
    fontWeight: 700 as any,
    lineHeight: 1.2,
  },
  heartbeatLabel: {
    color: tokens.colorNeutralForeground3,
  },

  /* Hex loop wrapper */
  hexWrapper: {
    position: 'relative',
    width: '100%',
    maxWidth: '720px',
    height: '520px',
    margin: '0 auto',
    '@media (max-width: 700px)': {
      height: '480px',
      maxWidth: '400px',
    },
  },

  /* Individual stage node */
  hexNode: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    width: '160px',
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    zIndex: 2,
    textAlign: 'center' as const,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  hexIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // 22px: no exact Fluent fontSize token (20/24 are the neighbours) — left as-is.
    fontSize: '22px',
    color: tokens.colorNeutralForegroundStaticInverted,
    flexShrink: 0,
  },
  hexMetric: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },

  /* SVG overlay for connectors */
  svgOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1,
  },

  /* Governance model section */
  modelCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingVerticalL,
    flexWrap: 'wrap',
  },
  modelPart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    minWidth: '140px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  modelPlus: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  modelEquals: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: 600,
    color: governanceTokens.colorGovernancePrimary,
  },
});

/* ------------------------------------------------------------------ */
/*  Hex layout positions (percentages of the container)                */
/*  Arranged clockwise from top-center:                                */
/*    0: top-center       (Signals)                                    */
/*    1: top-right        (Policy)                                     */
/*    2: bottom-right     (Enforcement)                                */
/*    3: bottom-center    (Evidence)                                   */
/*    4: bottom-left      (Assurance)                                  */
/*    5: top-left         (Intelligence)                               */
/* ------------------------------------------------------------------ */
// Absolute pixel offsets for the hexagonal loop diagram's node layout math
// (matched 1:1 with HEX_CENTERS' SVG coordinate space below) — not a spacing-
// rhythm value, so left as raw literals rather than forced into the token scale.
const HEX_POSITIONS: { left: string; top: string }[] = [
  { left: 'calc(50% - 80px)', top: '0px' },         // 0  top-center
  { left: 'calc(85% - 80px)', top: '110px' },        // 1  top-right
  { left: 'calc(85% - 80px)', top: '310px' },        // 2  bottom-right
  { left: 'calc(50% - 80px)', top: '410px' },        // 3  bottom-center
  { left: 'calc(15% - 80px)', top: '310px' },        // 4  bottom-left
  { left: 'calc(15% - 80px)', top: '110px' },        // 5  top-left
];

/* SVG center points for drawing connectors (matching HEX_POSITIONS) */
const HEX_CENTERS = [
  { x: 360, y: 60 },   // 0  top-center
  { x: 612, y: 170 },  // 1  top-right
  { x: 612, y: 370 },  // 2  bottom-right
  { x: 360, y: 470 },  // 3  bottom-center
  { x: 108, y: 370 },  // 4  bottom-left
  { x: 108, y: 170 },  // 5  top-left
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export const GovernanceLoopPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const navigate = useNavigate();
  const { data } = useDashboardOverview();

  const _repoCount = data?.integrationHealth?.totalIntegrations ?? 0;

  /* ------ Stages ------ */
  const stages: LoopStage[] = [
    {
      id: 'signals',
      label: 'Governance Signals',
      icon: <PlugConnectedRegular />,
      color: STAGE_COLORS.signals,
      route: '/scanners',
      metrics: [
        { label: 'Events/min', value: '221' },
        { label: 'Collectors', value: String(_repoCount || 12) },
      ],
    },
    {
      id: 'policy',
      label: 'Policy Engine',
      icon: <ShieldCheckmarkRegular />,
      color: STAGE_COLORS.policy,
      route: '/guardrails',
      metrics: [
        { label: 'Evals/sec', value: '33.5' },
        { label: 'Active rules', value: '148' },
      ],
    },
    {
      id: 'enforcement',
      label: 'Runtime Enforcement',
      icon: <ShieldLockRegular />,
      color: STAGE_COLORS.enforcement,
      route: '/control-room',
      metrics: [
        { label: 'Blocked (24h)', value: '71' },
        { label: 'Warnings', value: '214' },
      ],
    },
    {
      id: 'evidence',
      label: 'Evidence Chain',
      icon: <CertificateRegular />,
      color: STAGE_COLORS.evidence,
      route: '/evidence',
      metrics: [
        { label: 'Artifacts (24h)', value: '13,284' },
        { label: 'Verified', value: '100%' },
      ],
    },
    {
      id: 'assurance',
      label: 'Framework Assurance',
      icon: <GridRegular />,
      color: STAGE_COLORS.assurance,
      route: '/frameworks',
      metrics: [
        { label: 'Coverage', value: '55%' },
        { label: 'Frameworks', value: '4' },
      ],
    },
    {
      id: 'intelligence',
      label: 'Governance Intelligence',
      icon: <LightbulbRegular />,
      color: STAGE_COLORS.intelligence,
      route: '/ai-advisor',
      metrics: [
        { label: 'Attack paths', value: '3' },
        { label: 'Recommendations', value: '12' },
      ],
    },
  ];

  /* ------ Heartbeat metrics ------ */
  const heartbeat = [
    { label: 'Risks detected (24h)', value: '13,284', color: STAGE_COLORS.signals },
    { label: 'Enforcement decisions', value: '48,212', color: STAGE_COLORS.policy },
    { label: 'Enforcement actions', value: '71', color: STAGE_COLORS.enforcement },
    { label: 'Proof records', value: '13,284', color: STAGE_COLORS.evidence },
    { label: 'Framework coverage', value: '55%', color: STAGE_COLORS.assurance },
    { label: 'Attack paths detected', value: '3', color: STAGE_COLORS.intelligence },
  ];

  /* ------ Build SVG connector paths ------ */
  const connectorPaths: { d: string; color: string; id: string }[] = [];
  for (let i = 0; i < 6; i++) {
    const from = HEX_CENTERS[i];
    const to = HEX_CENTERS[(i + 1) % 6];
    const mx = (from.x + to.x) / 2;
    const my = (from.y + to.y) / 2;
    connectorPaths.push({
      d: `M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`,
      color: stages[i].color,
      id: `path-${i}`,
    });
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Governance Loop"
        subtitle="Live enforcement activity across your software delivery lifecycle"
      />

      {/* ---- System Heartbeat ---- */}
      <Card className={styles.heartbeatCard}>
        <CardHeader
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <HeartPulseRegular style={{ fontSize: tokens.fontSizeBase500, color: governanceTokens.colorGovernancePrimary }} />
              <Title2>System Heartbeat</Title2>
            </div>
          }
          description="Aggregate telemetry from the last 24 hours"
        />
        <div className={styles.heartbeatGrid}>
          {heartbeat.map((h) => (
            <div key={h.label} className={styles.heartbeatItem}>
              <span className={styles.heartbeatValue} style={{ color: h.color }}>
                {h.value}
              </span>
              <Caption1 className={styles.heartbeatLabel}>{h.label}</Caption1>
            </div>
          ))}
        </div>
      </Card>

      {/* ---- Runtime Governance Engine ---- */}
      <Card>
        <CardHeader
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <ArrowSyncRegular
                style={{
                  fontSize: tokens.fontSizeBase500,
                  color: governanceTokens.colorGovernancePrimary,
                  animation: 'glPulse 3s ease-in-out infinite',
                }}
              />
              <Title2>Runtime Governance Engine</Title2>
            </div>
          }
          description="Each stage feeds the next -- creating a compounding governance loop that strengthens over time."
        />
        <div style={{ padding: tokens.spacingHorizontalL }}>
          <div className={styles.hexWrapper}>
            {/* SVG connectors with animated dots */}
            <svg
              className={styles.svgOverlay}
              viewBox="0 0 720 520"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
            >
              <defs>
                {connectorPaths.map((cp) => (
                  <path key={cp.id} id={cp.id} d={cp.d} />
                ))}
              </defs>

              {/* Connector lines */}
              {connectorPaths.map((cp) => (
                <use
                  key={`line-${cp.id}`}
                  href={`#${cp.id}`}
                  stroke={cp.color}
                  strokeWidth="2"
                  strokeOpacity="0.35"
                  fill="none"
                />
              ))}

              {/* Animated dots flowing along each path */}
              {connectorPaths.map((cp, idx) => (
                <React.Fragment key={`dots-${cp.id}`}>
                  <circle r="4" fill={cp.color} opacity="0">
                    <animateMotion
                      dur="3s"
                      repeatCount="indefinite"
                      begin={`${idx * 0.5}s`}
                    >
                      <mpath href={`#${cp.id}`} />
                    </animateMotion>
                    <animate
                      attributeName="opacity"
                      values="0;1;1;0"
                      keyTimes="0;0.1;0.9;1"
                      dur="3s"
                      repeatCount="indefinite"
                      begin={`${idx * 0.5}s`}
                    />
                  </circle>
                  <circle r="4" fill={cp.color} opacity="0">
                    <animateMotion
                      dur="3s"
                      repeatCount="indefinite"
                      begin={`${idx * 0.5 + 1.5}s`}
                    >
                      <mpath href={`#${cp.id}`} />
                    </animateMotion>
                    <animate
                      attributeName="opacity"
                      values="0;1;1;0"
                      keyTimes="0;0.1;0.9;1"
                      dur="3s"
                      repeatCount="indefinite"
                      begin={`${idx * 0.5 + 1.5}s`}
                    />
                  </circle>
                </React.Fragment>
              ))}
            </svg>

            {/* Stage nodes */}
            {stages.map((stage, idx) => (
              <div
                key={stage.id}
                className={styles.hexNode}
                style={{
                  left: HEX_POSITIONS[idx].left,
                  top: HEX_POSITIONS[idx].top,
                  borderLeft: `3px solid ${stage.color}`,
                  animation: 'glGlow 4s ease-in-out infinite',
                  animationDelay: `${idx * 0.6}s`,
                }}
                onClick={() => navigate(stage.route)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(stage.route);
                }}
              >
                <div className={styles.hexIcon} style={{ backgroundColor: stage.color }}>
                  {stage.icon}
                </div>
                <Body2 style={{ fontWeight: 600 }}>{stage.label}</Body2>
                <div className={styles.hexMetric}>
                  {stage.metrics.map((m) => (
                    <Caption1 key={m.label} style={{ color: tokens.colorNeutralForeground3 }}>
                      <Badge
                        appearance="outline"
                        size="small"
                        color="informative"
                        style={{ marginRight: tokens.spacingHorizontalXS }}
                      >
                        {m.value}
                      </Badge>
                      {m.label}
                    </Caption1>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* ---- Governance Model ---- */}
      <Card>
        <CardHeader
          header={<Title3>Governance Model</Title3>}
          description="The living knowledge base that encodes your organization's governance posture"
        />
        <div className={styles.modelCard}>
          <div
            className={styles.modelPart}
            onClick={() => navigate('/graph')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate('/graph');
            }}
          >
            <ShareAndroidRegular style={{ fontSize: tokens.fontSizeHero700, color: solutionTokens.brandSecondary }} />
            <Body1 style={{ fontWeight: 600 }}>Knowledge Graph</Body1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Entity relationships</Caption1>
          </div>
          <span className={styles.modelPlus}>+</span>
          <div
            className={styles.modelPart}
            onClick={() => navigate('/twin')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate('/twin');
            }}
          >
            <ShareAndroidRegular style={{ fontSize: tokens.fontSizeHero700, color: solutionTokens.brandSecondary }} />
            <Body1 style={{ fontWeight: 600 }}>Digital Twin</Body1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Posture simulation</Caption1>
          </div>
          <span className={styles.modelPlus}>+</span>
          <div
            className={styles.modelPart}
            onClick={() => navigate('/constitution')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate('/constitution');
            }}
          >
            <ShareAndroidRegular style={{ fontSize: tokens.fontSizeHero700, color: solutionTokens.brandSecondary }} />
            <Body1 style={{ fontWeight: 600 }}>Constitution</Body1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Security principles</Caption1>
          </div>
          <span className={styles.modelEquals}>=</span>
          <div
            style={{
              padding: tokens.spacingVerticalM,
              borderRadius: tokens.borderRadiusMedium,
              background: `linear-gradient(135deg, ${governanceTokens.colorGovernancePrimary}, ${governanceTokens.colorGovernanceSecondary})`,
              minWidth: '160px',
              textAlign: 'center',
            }}
          >
            <Body1 style={{ fontWeight: 600, color: tokens.colorNeutralForegroundStaticInverted }}>Governance Model</Body1>
            {/* rgba(255,255,255,0.8) === the static-inverted (white) token at ~80% alpha (hex CC). */}
            <Caption1 style={{ color: `${tokens.colorNeutralForegroundStaticInverted}CC` }}>Living governance posture</Caption1>
          </div>
        </div>
      </Card>
    </div>
  );
};
